import axios from "axios";
import {sendMessage} from "../../../context/utils";

type DeviceInfo = {
    httpBrowserLanguage: string;
    httpBrowserJavaEnabled: boolean;
    httpBrowserJavaScriptEnabled: boolean;
    httpBrowserColorDepth: string;
    httpBrowserScreenHeight: string;
    httpBrowserScreenWidth: string;
    httpBrowserTimeDifference: string;
    userAgentBrowserValue: string;
    deviceChannel: string;
}

export const baseUrl = process.env.REACT_APP_API_URL;

export const generateApiUrl = (currency: string, auth?: string) => {
    if (!baseUrl) {
        throw new Error('Base URL is not defined');
    }

    if (auth === 'pin') {
        return `${baseUrl}pay/${currency}/cardpayment/v2`;
    } else if (auth === 'otp') {
        return `${baseUrl}pay/${currency}/cardpayment/v2`;
    } else if (!auth) {
        return `${baseUrl}pay/${currency}/cardpayment/v2`;
    }

    return `${baseUrl}pay/${currency}/charge`;
};

export const collectDeviceInfo = (): DeviceInfo => {
    return {
        httpBrowserLanguage: window.navigator.language || '',
        httpBrowserJavaEnabled: Boolean(window.navigator.javaEnabled()),
        httpBrowserJavaScriptEnabled: true,
        httpBrowserColorDepth: window.screen.colorDepth.toString(),
        httpBrowserScreenHeight: window.screen.height.toString(),
        httpBrowserScreenWidth: window.screen.width.toString(),
        httpBrowserTimeDifference: (new Date().getTimezoneOffset() * -1).toString(),
        userAgentBrowserValue: window.navigator.userAgent,
        deviceChannel: 'Browser'
    };
};

const checkFor3DSCompletion = async (
    txRef: string,
    merchantKey: string,
    onShowAlert: ((error: any, message: string) => void) | undefined,
    setTransactionStatus: (value: string) => void
) => {
    try {
        const response = await axios(`${baseUrl}pay/3ds/auth?reference=${txRef}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'x-auth-token': merchantKey,
                ...(process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN && {
                    'x-fe-token': process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN,
                }),
            },
        });

        const status = response.data.status;

        if (status === 'pending') {
            setTransactionStatus('pending');
            onShowAlert?.('Info', 'Verification is still pending. Please try again later.');
        }

        return response.data;

    } catch (error: any) {
        onShowAlert?.(error, 'Error fetching verification status');
        setTransactionStatus('error');
    }
};




export const handle3DSRedirectFlow = async (
    transactionId: string,
    txRef: string,
    merchantKey: string,
    setTransactionStatus: any,
    setPageTransactionStatus: any,
    onShowAlert?: (error: any, message: string) => void
) => {
    try {
        const redirectResponse = await axios(`${process.env.REACT_APP_API_URL}pay/3ds/redirect?id=${transactionId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'x-auth-token': merchantKey,
                ...(process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN && {
                    'x-fe-token': process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN,
                }),
            },
        });

        const { ACSUrl, TermUrl, MD, jwt } = redirectResponse.data.data;

        document.body.innerHTML = `
            <div style="
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                background-color: white;
                display: flex;
                align-items: center;
                justify-content: center;
                font-family: sans-serif;
                font-size: 16px;
                color: #333;
            ">
                Redirecting to secure payment...
            </div>
        `;

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = ACSUrl;
        form.style.display = 'none';

        [
            ['JWT', jwt],
            ['MD', MD],
            ['TermUrl', TermUrl],
        ].forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();

        const result = await checkFor3DSCompletion(txRef, merchantKey, onShowAlert, setTransactionStatus);
        const isSuccess = result?.status === 'successful';

        setTransactionStatus(isSuccess ? 'success' : 'error');
        setPageTransactionStatus(isSuccess ? 'success' : 'error');

        if (!isSuccess) {
            onShowAlert?.("error", "3DS Authentication Failed!");
        }

        sendMessage({
            status: isSuccess ? 'successful' : 'ERROR',
            tnxRef: txRef,
        });

    } catch (error: any) {
        onShowAlert?.(error, error.message ?? "Unexpected error occurred");
        setTransactionStatus('error');
    }
};

